import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './App.css';
import ContractManager from './components/features/contracts/ContractManager';
import Login from './components/features/auth/Login';
import TestEnhancedReview from './components/features/review/TestEnhancedReview';
import TestJsonLoader from './components/features/TestJsonLoader';
import UserProfile from './pages/Settings/UserProfile';
import AccountSettings from './pages/Settings/AccountSettings';
import HelpCenter from './pages/HelpCenter';
import { UserSettings } from './components/features/admin/UserSettings';
import { AdminDashboard } from './components/features/admin/AdminDashboard';
import { MasterDataPage } from './pages/MasterDataPage';
import { FileUploadTest } from './components/features/upload/FileUploadTest';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { usePermissions } from './hooks/usePermissions';
import { queryClient } from './lib/queryClient';

// Legal AI Workspace
import { LegalAssistanceWorkspace } from './components/features/legal-ai';

// Document Generator
import { DocumentGeneratorWorkspace } from './components/features/document-generator';

// Contract Workflow imports
import { ContractWorkflowPage } from './components/features/contract-workflow/ContractWorkflowPage';
import {
  Step1_ContractOverview,
  Step2_Analysis,
  Step3_ClauseEditing,
  Step4_Summary,
  Step5_Negotiation
} from './components/features/contract-workflow/steps';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Admin Route Component (Legacy - kept for compatibility)
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.role !== 'Admin') {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

// Permission-based Route Component
export const PermissionRoute: React.FC<{
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
}> = ({ children, permission, permissions = [], requireAll = false }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  }

  if (!hasAccess) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
          <div className="App bg-bg-primary min-h-screen text-text-primary transition-colors duration-300">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/test-enhanced-review" element={<TestEnhancedReview />} />
              <Route path="/test-enhanced" element={<TestEnhancedReview />} />
              <Route path="/test-json" element={<TestJsonLoader />} />
              <Route
                path="/settings/profile"
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/account"
                element={
                  <ProtectedRoute>
                    <AccountSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <UserSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/help"
                element={
                  <ProtectedRoute>
                    <HelpCenter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <PermissionRoute permission="users.view">
                    <AdminDashboard />
                  </PermissionRoute>
                }
              />
              <Route
                path="/master-data"
                element={
                  <PermissionRoute permission="masterdata.view">
                    <MasterDataPage />
                  </PermissionRoute>
                }
              />
              {/* Legal AI Workspace */}
              <Route
                path="/legal-ai"
                element={
                  <ProtectedRoute>
                    <LegalAssistanceWorkspace />
                  </ProtectedRoute>
                }
              />
              {/* Document Generator Workspace */}
              <Route
                path="/document-generator"
                element={
                  <ProtectedRoute>
                    <DocumentGeneratorWorkspace />
                  </ProtectedRoute>
                }
              />
              {/* Contract Workflow Routes */}
              <Route
                path="/contracts/:id"
                element={
                  <ProtectedRoute>
                    <ContractWorkflowPage />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<Step1_ContractOverview />} />
                <Route path="analysis" element={<Step2_Analysis />} />
                <Route path="clauses" element={<Step3_ClauseEditing />} />
                <Route path="summary" element={<Step4_Summary />} />
                <Route path="negotiation" element={<Step5_Negotiation />} />
              </Route>
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <ContractManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/file-upload-test"
                element={
                  <ProtectedRoute>
                    <FileUploadTest />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
